import { defineStore } from 'pinia';
import { axios } from '@boot/axios.js';
import { useSharedStore } from '@stores/shared.js';
/**
 * Return the default state for the assets store.
 */
function getDefaultState() {
    return {
        assets: {},
        selectedAsset: null,
        statuses: {},
        assetGroups: {},
        storedAssetGroupsPerFacility: {}
    };
}
/**
 * Formats an asset object with specific properties.
 */
function formatAsset(asset) {
    return {
        id: asset.id,
        uuid: asset.uuid,
        facility: asset.facility,
        groups: asset.groups,
        name: asset.name,
        description: asset.description,
        max_downtime_delay: asset.max_downtime_delay,
        max_downtime_justification: asset.max_downtime_justification,
        sensor_configuration: asset.sensor_configuration,
        has_proximity_sensor: asset.has_proximity_sensor,
        proximity_sensor_use: asset.proximity_sensor_use,
        products: asset.products,
        causes: asset.causes,
        dashboard: asset.dashboard,
        speed_widget_color_levels: asset.speed_widget_color_levels,
        use_packaging_factors: asset.use_packaging_factors,
        allow_downtime_dismissal: asset.allow_downtime_dismissal,
        extended_downtime_delay: asset.extended_downtime_delay,
        fault_code_window: asset.fault_code_window,
        is_deleted: asset.is_deleted,
        enable_quality_event_recording: asset.enable_quality_event_recording,
        acceptable_percentage_rejects: asset.acceptable_percentage_rejects,
        acceptable_reject_sample_size: asset.acceptable_reject_sample_size,
        quality_defect_window_s: asset.quality_defect_window_s
    };
}
/**
 * Formats a phase object into a standardized asset phase object.
 */
function formatAssetPhase(phase) {
    return {
        id: phase.id,
        phaseUUID: phase.uuid,
        assetId: phase.asset,
        status: phase.phase,
        cause: phase.cause,
        currentProduct: phase.product,
        currentProductName: phase.product_name,
        start: phase.start,
        lockedTransitionToRunning: phase.locked_transition_to_running
    };
}
/**
 * Store for managing asset-related data.
 */
export const useAssetsStore = defineStore('assets', {
    state: () => getDefaultState(),
    getters: {
        /**
         * Get asset list from a specific facility.
         */
        getAssets: state => {
            return (facilityId, withDeleted = false) => {
                const assets = Object.values(state.assets).sort((a, b) => a.name.localeCompare(b.name));
                const fId = Number(facilityId);
                // Deleted assets are not included by default
                if (!withDeleted) {
                    return assets.filter(asset => asset.facility === fId && !asset.is_deleted);
                }
                // Return all assets including deleted
                return assets.filter(asset => asset.facility === fId);
            };
        },
        /**
         * Get a single asset by id.
         */
        getAsset: state => {
            return (id) => {
                const assetId = Number(id);
                if (state.assets[assetId]) {
                    return state.assets[assetId];
                }
                else {
                    return null;
                }
            };
        },
        /**
         * Get a single asset by UUID.
         */
        getAssetByUUID: state => {
            return (uuid) => {
                for (const asset of Object.values(state.assets)) {
                    if (asset.uuid === uuid) {
                        return asset;
                    }
                }
                return null;
            };
        },
        /**
         * Retrieves the name of an asset given its ID.
         */
        getAssetName: state => {
            return (assetId) => {
                const id = Number(assetId);
                if (state.assets[id]) {
                    return state.assets[id].name;
                }
                else {
                    return null;
                }
            };
        },
        /**
         * Get the current facility selected.
         */
        getSelectedAsset: state => {
            if (state.selectedAsset) {
                return state.assets[state.selectedAsset];
            }
            else {
                return null;
            }
        },
        /**
         * Get asset phase details.
         */
        getAssetPhase: state => {
            return (id) => {
                const assetId = Number(id);
                return state.statuses[assetId] || null;
            };
        },
        /**
         * Get asset status by asset id.
         */
        getAssetStatus: state => {
            return (id) => {
                const assetId = Number(id);
                if (state.statuses[assetId]) {
                    return state.statuses[assetId]['status'];
                }
                else {
                    return null;
                }
            };
        },
        /**
         * Get current product running on an asset by asset id.
         */
        getAssetProduct: state => {
            return (id) => {
                const assetId = Number(id);
                if (state.statuses[assetId]) {
                    return state.statuses[assetId]['currentProduct'];
                }
                else {
                    return null;
                }
            };
        },
        /**
         * Get the current cause of the asset.
         */
        getAssetCause: state => {
            return (id) => {
                const assetId = Number(id);
                if (state.statuses[assetId]) {
                    return state.statuses[assetId]['cause'];
                }
                else {
                    return null;
                }
            };
        },
        /**
         * Get Asset groups of assets in the facility.
         */
        getAssetGroups: state => {
            return (facilityId) => {
                const fId = Number(facilityId);
                // select the groups that belong to this facility
                const groups = Object.values(state.assetGroups).filter(group => {
                    return Number(group.facility) === fId;
                });
                // order the list by name
                return groups.sort((a, b) => a.name.localeCompare(b.name));
            };
        },
        /**
         * Retrieve an asset group from store.
         */
        getGroup: state => {
            return (groupId) => {
                const id = Number(groupId);
                return state.assetGroups[id];
            };
        },
        /**
         * Retrieve stored asset groups for particular facility.
         */
        getStoredAssetGroupsPerFacility: state => {
            return (facilityId) => {
                const fId = Number(facilityId);
                const storedAssetGroupsPerFacility = state.storedAssetGroupsPerFacility[fId];
                if (storedAssetGroupsPerFacility) {
                    return storedAssetGroupsPerFacility;
                }
                return [];
            };
        }
    },
    actions: {
        /**
         * Reset store state.
         */
        resetState() {
            this.$reset();
        },
        /**
         * Load assets for a specific facility.
         */
        async loadAssetsAction(payload) {
            const { facilityId, showLoading = true } = payload;
            try {
                // begin loading
                if (showLoading) {
                    useSharedStore().beginLoading('Loading assets');
                }
                // load assets
                const response = await axios.get(`assets/?facility=${facilityId}&with_deleted=true`);
                const assets = response.data.results;
                // set assets for this facility
                assets.forEach((asset) => {
                    const formattedAsset = formatAsset(asset);
                    this.assets[formattedAsset.id] = { ...formattedAsset };
                });
                // Extract the list of groups from the list of assets
                this.addAssetGroupsAction(assets, facilityId);
                // Remove from failed loads if present
                const { useFacilitiesStore } = await import('@stores/facilities');
                useFacilitiesStore().removeLoadingAssetsFailedAction(facilityId);
            }
            catch (error) {
                console.error('Failed to load assets due to: ', error);
                throw error;
            }
            finally {
                // end loading
                if (showLoading) {
                    useSharedStore().stopLoading();
                }
            }
        },
        /**
         * Loads the current phases of an asset or facility.
         */
        async loadCurrentPhasesAction(payload) {
            const { facilityId = null, assetId = null } = payload;
            // Check if the facilityId or assetId is provided
            if (!facilityId && !assetId) {
                console.error('Failed to load asset phases due to: ', 'Either facilityId or assetId must be provided');
                return;
            }
            // Create URL with dynamic query parameters using URLSearchParams
            const params = new URLSearchParams();
            if (facilityId) {
                params.append('facility', String(facilityId));
            }
            if (assetId) {
                params.append('asset', String(assetId));
            }
            const url = `/assetphases/current/?${params.toString()}`;
            try {
                const response = await axios.get(url);
                const phases = response.data.results;
                phases.forEach((phase) => {
                    const formattedPhase = formatAssetPhase(phase);
                    // Set the status for the asset
                    this.statuses[formattedPhase.assetId] = {
                        ...formattedPhase
                    };
                });
            }
            catch (error) {
                console.error('Failed to load asset phases due to: ', error);
                throw error;
            }
        },
        /**
         * Update asset.
         */
        async updateAssetAction(asset) {
            try {
                await axios.put(`assets/${asset.id}/`, asset);
                // Update in store
                this.assets[asset.id] = { ...asset };
            }
            catch (error) {
                console.error('Failed to update asset due to: ', error);
                throw error;
            }
        },
        /**
         * Select an asset.
         */
        selectAssetAction(assetId) {
            this.selectedAsset = Number(assetId);
        },
        /**
         * Set the asset phase.
         */
        setAssetPhaseAction(data) {
            const assetId = data.assetId;
            // Initialize status if not exists
            if (!(assetId in this.statuses)) {
                this.statuses[assetId] = {
                    id: 0,
                    phaseUUID: '',
                    assetId: assetId,
                    status: 'unknown',
                    cause: null,
                    currentProduct: null,
                    currentProductName: null,
                    start: '',
                    lockedTransitionToRunning: false
                };
            }
            // Update status
            this.statuses[assetId] = { ...this.statuses[assetId], ...data };
        },
        /**
         * Set current downtime cause for an asset.
         */
        setDowntimeCauseAction(payload) {
            const { assetId, causeId } = payload;
            if (this.statuses[assetId]) {
                this.statuses[assetId].cause = causeId;
            }
        },
        /**
         * Load asset groups for a specific facility.
         */
        addAssetGroupsAction(assets, facilityId) {
            const fId = Number(facilityId);
            const groupMap = {};
            assets.forEach(asset => {
                if (asset.facility === fId) {
                    for (const group of asset.groups) {
                        if (!(group.id in groupMap)) {
                            groupMap[group.id] = {
                                id: group.id,
                                facility: fId,
                                name: group.name,
                                assets: [asset.id]
                            };
                        }
                        else {
                            groupMap[group.id].assets.push(asset.id);
                        }
                    }
                }
            });
            Object.values(groupMap).forEach(group => {
                this.assetGroups[group.id] = { ...group };
            });
        },
        /**
         * Remove an asset group from store.
         */
        removeAssetGroupAction(groupId) {
            const id = Number(groupId);
            const group = this.assetGroups[id];
            if (group) {
                // update each asset that is part of the group
                group.assets.forEach(assetId => {
                    if (this.assets[assetId]) {
                        this.assets[assetId].groups = this.assets[assetId].groups.filter(g => g.id !== id);
                    }
                });
                delete this.assetGroups[id];
            }
        },
        /**
         * Add an asset group to store.
         */
        addAssetGroupAction(group) {
            this.assetGroups[group.id] = { ...group };
            // update each asset that is part of the new group
            group.assets.forEach(assetId => {
                if (this.assets[assetId]) {
                    this.assets[assetId].groups = [
                        ...this.assets[assetId].groups,
                        { id: group.id, name: group.name }
                    ];
                }
            });
        },
        /**
         * Update asset group in store.
         */
        updateAssetGroupAction(group) {
            const prev_group = this.assetGroups[group.id];
            if (prev_group) {
                const removedAssetIds = prev_group.assets.filter(x => !group.assets.includes(x));
                const addedAssetIds = group.assets.filter(x => !prev_group.assets.includes(x));
                removedAssetIds.forEach(assetId => {
                    if (this.assets[assetId]) {
                        this.assets[assetId].groups = this.assets[assetId].groups.filter(x => x.id !== group.id);
                    }
                });
                addedAssetIds.forEach(assetId => {
                    if (this.assets[assetId]) {
                        this.assets[assetId].groups = [
                            ...this.assets[assetId].groups,
                            { id: group.id, name: group.name }
                        ];
                    }
                });
                this.assetGroups[group.id] = { ...group };
            }
        },
        /**
         * Store state of asset group filter for each facility in store.
         */
        addSelectedGroupPerFacilityAction(payload) {
            const { facilityId, selectedAssetGroup } = payload;
            const fId = Number(facilityId);
            if (fId in this.storedAssetGroupsPerFacility) {
                // if facility already exists, update with newly selected asset group
                this.storedAssetGroupsPerFacility[fId] = [
                    ...this.storedAssetGroupsPerFacility[fId],
                    { ...selectedAssetGroup }
                ];
            }
            else {
                // if facility does not exist, initialize with the asset group
                this.storedAssetGroupsPerFacility[fId] = [
                    { ...selectedAssetGroup }
                ];
            }
        },
        /**
         * Update the state of asset group filter for each facility in store.
         */
        removeSelectedGroupPerFacilityAction(payload) {
            const { facilityId, removedAssetGroup } = payload;
            const fId = Number(facilityId);
            if (fId in this.storedAssetGroupsPerFacility) {
                // Remove the asset group from the selected groups
                this.storedAssetGroupsPerFacility[fId] =
                    this.storedAssetGroupsPerFacility[fId].filter(assetGroup => assetGroup.id !== removedAssetGroup.id);
            }
        }
    }
});
